import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useCurrentPatient } from '@/hooks/useCurrentPatient';
import { usePortalMessages } from '@/hooks/usePortalMessages';
import { usePortalActiveClinic } from '@/lib/portalActiveClinic';
import { formatClinicAddress } from '@/lib/portalFormat';
import { ClinicFallbackMark, PortalIcon, type PortalIconName } from '@/components/portal/PortalIcon';
import {
  isPortalNavCurrent,
  PORTAL_ACCOUNT_NAV,
  PORTAL_MORE_NAV,
  PORTAL_TAB_NAV,
  PORTAL_TOP_NAV,
} from '@/components/portal/portalNav';
import { isUnreadClinicMessage, patientInitials } from '@/components/portal/dashboard/portalDashboardUtils';
import { useTheme } from '@/lib/theme';

function BrandMark({
  logoUrl,
  name,
  subtitle,
}: {
  logoUrl: string | null | undefined;
  name: string;
  subtitle: string;
}) {
  return (
    <>
      {logoUrl ? <img className="cl-logo" src={logoUrl} alt="" /> : <ClinicFallbackMark />}
      <div>
        <b>{name}</b>
        <span>{subtitle}</span>
      </div>
    </>
  );
}

function NavBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return <span className="badge">{count > 9 ? '9+' : count}</span>;
}

function DesktopMoreMenu({ pathname }: { pathname: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const moreCurrent = isPortalNavCurrent(pathname, '/portal/more');

  useEffect(() => {
    if (!open) return;
    const onDoc = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div ref={ref} className="menu-wrap">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-current={moreCurrent ? 'page' : undefined}
        onClick={() => setOpen((value) => !value)}
      >
        Mere <PortalIcon name="chevronDown" size="xs" />
      </button>
      {open ? (
        <div role="menu" className="card pt-menu">
          {PORTAL_MORE_NAV.map((item) => (
            <Link
              key={item.href}
              role="menuitem"
              to={item.href}
              aria-current={isPortalNavCurrent(pathname, item.href) ? 'page' : undefined}
              onClick={() => setOpen(false)}
            >
              <PortalIcon name={item.icon} size="sm" />
              {item.label}
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function PortalTopBar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { signOut, user } = useAuth();
  const { data: patient } = useCurrentPatient();
  const { clinics, activeClinicId } = usePortalActiveClinic();
  const { data: messages = [] } = usePortalMessages(activeClinicId);
  const { preference, setPreference } = useTheme();
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);

  const activeClinic = clinics.find((clinic) => clinic.clinic_id === activeClinicId);
  const unreadCount = messages.filter(isUnreadClinicMessage).length;
  const name = [patient?.first_name, patient?.last_name].filter(Boolean).join(' ') || user?.email || 'Konto';
  const initials = patientInitials(patient?.first_name, patient?.last_name);

  useEffect(() => {
    if (!accountOpen) return;
    const onDoc = (event: MouseEvent) => {
      if (!accountRef.current?.contains(event.target as Node)) setAccountOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [accountOpen]);

  const handleLogout = async () => {
    try {
      await signOut();
    } finally {
      navigate('/portal/login');
    }
  };

  return (
    <header className="pt-top">
      <div className="in">
        <Link
          to="/portal/dashboard"
          className="cl-brand"
          aria-label={`${activeClinic?.clinic_name ?? 'Klinik'} – forside`}
        >
          <BrandMark
            logoUrl={activeClinic?.clinic_logo}
            name={activeClinic?.clinic_name ?? 'Clinio'}
            subtitle="Patientportal"
          />
        </Link>
        <nav className="pt-navlinks" aria-label="Portalmenu">
          {PORTAL_TOP_NAV.map((item) => {
            const current = isPortalNavCurrent(pathname, item.href);
            return (
              <Link key={item.href} to={item.href} aria-current={current ? 'page' : undefined}>
                {item.label}
                {item.badge === 'messages' ? <NavBadge count={unreadCount} /> : null}
              </Link>
            );
          })}
          <DesktopMoreMenu pathname={pathname} />
        </nav>
        <div ref={accountRef} className="menu-wrap">
          <button
            type="button"
            className="pt-user"
            aria-haspopup="menu"
            aria-expanded={accountOpen}
            aria-label={`Konto: ${name}`}
            onClick={() => setAccountOpen((value) => !value)}
          >
            <span className="avatar av32">{initials}</span>
            <b>{patient?.first_name?.trim() || name}</b>
            <PortalIcon name="chevronDown" size="xs" />
          </button>
          {accountOpen ? (
            <div role="menu" className="card pt-menu">
              <div className="who">
                <div className="help">Logget ind som</div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{user?.email}</div>
              </div>
              {PORTAL_ACCOUNT_NAV.map((item) => (
                <Link
                  key={item.href}
                  role="menuitem"
                  to={item.href}
                  aria-current={isPortalNavCurrent(pathname, item.href) ? 'page' : undefined}
                  onClick={() => setAccountOpen(false)}
                >
                  <PortalIcon name={item.icon} size="sm" />
                  {item.label}
                </Link>
              ))}
              <button
                type="button"
                role="menuitem"
                className="mi"
                onClick={() => {
                  setPreference(preference === 'dark' ? 'light' : 'dark');
                  setAccountOpen(false);
                }}
              >
                {preference === 'dark' ? 'Lys tilstand' : 'Mørk tilstand'}
              </button>
              <button
                type="button"
                role="menuitem"
                className="mi danger"
                onClick={() => {
                  setAccountOpen(false);
                  void handleLogout();
                }}
              >
                <PortalIcon name="logout" size="sm" /> Log ud
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}

export function PortalFooter() {
  const { t } = useTranslation();
  const { clinics, activeClinicId } = usePortalActiveClinic();
  const clinic = clinics.find((item) => item.clinic_id === activeClinicId);
  const address = clinic ? formatClinicAddress(clinic.clinic_address) : '';
  const line = [clinic?.clinic_name, address].filter(Boolean).join(' · ');
  return (
    <footer className="pt-foot">
      <div className="in">
        <span>{line || t('portal.footer.fallback')}</span>
        <span className="pw">
          {t('portal.footer.powered')} <b>Clinio</b>
        </span>
      </div>
    </footer>
  );
}

export function PortalTabBar() {
  const { pathname } = useLocation();
  const { activeClinicId } = usePortalActiveClinic();
  const { data: messages = [] } = usePortalMessages(activeClinicId);
  const unreadCount = messages.filter(isUnreadClinicMessage).length;

  return (
    <nav className="tabbar" aria-label="Portalmenu">
      {PORTAL_TAB_NAV.map((item) => {
        const current = isPortalNavCurrent(pathname, item.href);
        return (
          <Link key={item.href} to={item.href} aria-current={current ? 'page' : undefined}>
            <span className="pip" />
            <PortalIcon name={item.icon as PortalIconName} />
            {item.label}
            {item.badge === 'messages' ? <NavBadge count={unreadCount} /> : null}
          </Link>
        );
      })}
    </nav>
  );
}

export function PortalPageHead({
  title,
  subtitle,
  action,
  greet,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  greet?: boolean;
}) {
  return (
    <div className={`pt-head${greet ? ' greet' : ''}`}>
      <div>
        <h1>{title}</h1>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      {action ? <div className="r">{action}</div> : null}
    </div>
  );
}

export function PortalAuthFooter({ poweredLabel }: { poweredLabel: string }) {
  return (
    <footer className="auth-foot">
      {poweredLabel} <b>Clinio</b>
    </footer>
  );
}
